import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { AuthHeroOverlay } from './AuthLayout';

// Nexus Core - 認証画面群 (Login / Register / AcceptInvite) の共通シェル
// 背景レイヤー (画像 + 白フィルタ + ドット + network-line + heroOverlay) を保持し、
// 子ルート間 (/login ↔ /register など) を遷移しても背景は再マウントされず連続性を保つ
// 子は Outlet として描画され、useOutletContext 経由で
// collapsing / maxWidth / heroOverlay を変更できる
export default function AuthShell() {
  const [collapsing, setCollapsing] = useState(false);
  const [maxWidth, setMaxWidth] = useState('528px');
  const [heroOverlay, setHeroOverlay] = useState(() => <AuthHeroOverlay />);

  // 背景レイヤーの初回フェードイン (Shell マウント時のみ、ページ切替では再発火しない)
  const [bgVisible, setBgVisible] = useState(false);
  useEffect(() => {
    let id2;
    const id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => setBgVisible(true));
    });
    return () => {
      cancelAnimationFrame(id1);
      if (id2 !== undefined) cancelAnimationFrame(id2);
    };
  }, []);

  const isBackgroundHidden = collapsing || !bgVisible;
  const verticalFadeMask =
    'linear-gradient(to bottom, transparent 0%, black 9%, black 91%, transparent 100%)';

  return (
    <div className="font-inter min-h-screen flex items-center justify-center md:justify-start relative overflow-hidden bg-light-neutral">
      <div
        className={`fixed inset-0 z-0 flex items-center overflow-hidden md:inset-auto md:top-3 md:right-3 md:bottom-3 md:justify-start transition-[left,opacity] duration-[600ms] ease-in-out ${
          collapsing ? 'md:left-[88px]' : 'md:left-[var(--auth-image-left)]'
        } ${isBackgroundHidden ? 'opacity-0' : 'opacity-100'}`}
        style={{ '--auth-image-left': `calc(${maxWidth} + 24px)` }}
        aria-hidden="true"
      >
        <img
          src="/images/login-bg.avif"
          alt=""
          className="w-full h-auto"
          style={{
            maskImage: verticalFadeMask,
            WebkitMaskImage: verticalFadeMask,
          }}
        />
      </div>
      <div
        className={`fixed inset-0 z-[1] bg-white/25 transition-opacity duration-[600ms] ease-in-out ${
          isBackgroundHidden ? 'opacity-0' : 'opacity-100'
        }`}
        aria-hidden="true"
      />
      <div
        className="fixed inset-0 z-[2] transition-opacity duration-[600ms] ease-in-out"
        style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, #C3C5D9 1px, transparent 0)',
          backgroundSize: '32px 32px',
          opacity: bgVisible ? 0.5 : 0,
        }}
        aria-hidden="true"
      />
      <div
        className={`network-line w-full top-1/4 left-0 -rotate-12 z-[3] transition-opacity duration-[600ms] ease-in-out ${
          isBackgroundHidden ? 'opacity-0' : ''
        }`}
      ></div>
      <div
        className={`network-line w-full top-3/4 left-0 rotate-12 z-[3] transition-opacity duration-[600ms] ease-in-out ${
          isBackgroundHidden ? 'opacity-0' : ''
        }`}
      ></div>

      {heroOverlay && (
        <div
          className={`hidden md:flex fixed top-1/2 right-3 bottom-3 z-[4] pointer-events-none flex-col justify-center px-10 lg:px-16 pb-[12vh] transition-[left,opacity] duration-[600ms] ease-in-out ${
            collapsing ? 'md:left-[88px]' : 'md:left-[var(--auth-image-left)]'
          } ${isBackgroundHidden ? 'opacity-0' : 'opacity-100'}`}
          style={{ '--auth-image-left': `calc(${maxWidth} + 24px)` }}
          aria-hidden="true"
        >
          {heroOverlay}
        </div>
      )}

      <Outlet
        context={{
          collapsing,
          setCollapsing,
          maxWidth,
          setMaxWidth,
          setHeroOverlay,
        }}
      />
    </div>
  );
}
